import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Compass, History, Music, ScrollText, ChevronRight } from 'lucide-react';
import MissionStatement from '@/components/MissionStatement';
import VisionStatement from '@/components/VisionStatement';
import HistorySection from '@/components/HistorySection';
import AnthemSection from '@/components/Anthem';

const About = () => {
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 1
  });

  const historyRef = useRef<HTMLDivElement>(null);
  const missionVisionRef = useRef<HTMLDivElement>(null);
  const anthemRef = useRef<HTMLDivElement>(null);

  const navItems = [
    { id: 'history', label: 'Our Story', icon: <History className="w-4 h-4" />, ref: historyRef },
    { id: 'vision', label: 'Mission & Vision', icon: <Compass className="w-4 h-4" />, ref: missionVisionRef },
    { id: 'anthem', label: 'Diocesan Anthem', icon: <Music className="w-4 h-4" />, ref: anthemRef },
  ];

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/about/history')) scrollToSection(historyRef);
    else if (path.includes('/about/vision-mission')) scrollToSection(missionVisionRef);
    else if (path.includes('/about/anthem')) scrollToSection(anthemRef);
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return (
    <div className="min-h-screen bg-[#fcfaf7]">
      {/* Reading Progress Bar */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-amber-500 z-[60] origin-left" style={{ scaleX }} />

      {/* Hero Header */}
      <div className="relative pt-32 pb-20 overflow-hidden bg-slate-900">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/natural-paper.png')]" />
        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-2 mb-6"
          >
            <span className="h-px w-8 bg-amber-500/50" />
            <span className="text-amber-500 uppercase tracking-[0.4em] text-[10px] font-bold">Ibadan South</span>
            <span className="h-px w-8 bg-amber-500/50" />
          </motion.div>
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6">
            Our Identity
          </h1>
          <p className="text-slate-400 font-serif text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            From our 1999 foundation to our shared vision for the future, 
            discover the heart of our spiritual community.
          </p>
        </div>
      </div>

      {/* Sticky Sub-Navigation */}
      <nav className="sticky top-16 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-4 md:gap-8 h-16 overflow-x-auto no-scrollbar">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.ref)}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-amber-600 transition-colors whitespace-nowrap px-2"
              >
                {item.icon}
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content Areas */}
      <div className="max-w-7xl mx-auto px-4 py-24 space-y-32">
        
        {/* Section: History */}
        <motion.section 
          ref={historyRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="flex items-center gap-4 mb-12">
            <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
               <History size={24} />
            </div>
            <h2 className="text-3xl font-serif font-bold text-slate-900">Historical Archives</h2>
          </div>
          <HistorySection />
        </motion.section>

        {/* Section: Vision & Mission */}
        <motion.section 
          ref={missionVisionRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="flex items-center gap-4 mb-12">
            <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
               <Compass size={24} />
            </div>
            <h2 className="text-3xl font-serif font-bold text-slate-900">Mandate & Purpose</h2>
          </div>
          <div className="space-y-12">
             <VisionStatement />
             <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
             <MissionStatement />
          </div>
        </motion.section>

        {/* Section: Anthem */}
        <motion.section 
          ref={anthemRef}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          className="pb-24"
        >
          <div className="flex items-center gap-4 mb-12">
            <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
               <Music size={24} />
            </div>
            <h2 className="text-3xl font-serif font-bold text-slate-900">Diocesan Anthem</h2>
          </div>
          <div className="bg-white rounded-[3rem] p-1 border border-slate-100 shadow-xl shadow-slate-200/50">
            <AnthemSection />
          </div>
        </motion.section>

      </div>

      {/* Footer Callout */}
      <div className="bg-slate-900 py-20 px-4 text-center text-white">
        <ScrollText className="w-12 h-12 text-amber-500 mx-auto mb-6 opacity-50" />
        <h3 className="text-2xl font-serif font-bold mb-4">Want to see our leadership?</h3>
        <p className="text-slate-400 mb-8 max-w-md mx-auto italic">Learn about the pioneers and current administrators leading our mission.</p>
        <button className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs transition-all">
          View Leadership <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default About;